import { fetchData } from 'utils/fetch';
import { restAPIs } from 'utils/restAPIs';

// JustWatch's own public GraphQL API (the same one justwatch.com's web app calls
// client-side). No key, no auth needed — but JustWatch's CORS policy only allows
// requests from justwatch.com/localhost, so this goes through our own backend's
// justwatchProxy.php passthrough rather than apis.justwatch.com directly.
export class JustWatchError extends Error {}

export const COUNTRIES = [
  { code: 'US', en: 'United States', ar: 'الولايات المتحدة' },
  { code: 'GB', en: 'United Kingdom', ar: 'المملكة المتحدة' },
  { code: 'AE', en: 'UAE', ar: 'الإمارات' },
  { code: 'SA', en: 'Saudi Arabia', ar: 'السعودية' },
  { code: 'EG', en: 'Egypt', ar: 'مصر' },
  { code: 'IN', en: 'India', ar: 'الهند' },
  { code: 'CA', en: 'Canada', ar: 'كندا' },
  { code: 'AU', en: 'Australia', ar: 'أستراليا' },
  { code: 'FR', en: 'France', ar: 'فرنسا' },
  { code: 'DE', en: 'Germany', ar: 'ألمانيا' },
];

const SEARCH_QUERY = `query GetSearchTitles($country: Country!, $language: Language!, $first: Int!, $filter: TitleFilter) {
  popularTitles(country: $country, first: $first, filter: $filter) {
    edges {
      node {
        id
        objectType
        content(country: $country, language: $language) {
          title
          fullPath
          originalReleaseYear
          externalIds { imdbId }
        }
        offers(country: $country, platform: WEB) {
          monetizationType
          standardWebURL
          retailPrice(language: $language)
          package { shortName clearName icon }
        }
      }
    }
  }
}`;

// JustWatch monetizationType -> a simpler kind we render, ranked best-first.
const KIND_BY_MONETIZATION = { FLATRATE: 'subscription', FREE: 'free', ADS: 'free', RENT: 'rent', BUY: 'buy' };
const KIND_RANK = { subscription: 0, free: 1, rent: 2, buy: 3 };

const iconUrl = (template) => {
  if (!template) return null;
  const path = template.replace('{profile}', 's100').replace('{format}', 'webp');
  return `https://images.justwatch.com${path.startsWith('/') ? '' : '/'}${path}`;
};

// One row per platform, each carrying every offer kind available on it
// (e.g. Apple TV often has both Rent and Buy), cheapest/best kind first.
const groupOffers = (offers) => {
  const byPlatform = new Map();
  (offers || []).forEach((offer) => {
    const pkg = offer.package;
    const kind = KIND_BY_MONETIZATION[offer.monetizationType];
    if (!pkg?.shortName || !kind) return;
    if (!byPlatform.has(pkg.shortName)) {
      byPlatform.set(pkg.shortName, {
        shortName: pkg.shortName,
        name: pkg.clearName,
        icon: iconUrl(pkg.icon),
        url: offer.standardWebURL,
        offers: new Map(),
      });
    }
    const platform = byPlatform.get(pkg.shortName);
    if (!platform.offers.has(kind)) platform.offers.set(kind, { kind, price: offer.retailPrice || null });
    if (!platform.url && offer.standardWebURL) platform.url = offer.standardWebURL;
  });
  return [...byPlatform.values()]
    .map((platform) => ({ ...platform, offers: [...platform.offers.values()].sort((a, b) => KIND_RANK[a.kind] - KIND_RANK[b.kind]) }))
    .sort((a, b) => KIND_RANK[a.offers[0].kind] - KIND_RANK[b.offers[0].kind]);
};

// Used only when we have no imdbId to confirm identity with: prefer an exact
// year match on an actual movie, tolerate a 1-year gap (regional release drift),
// otherwise fall back to JustWatch's own relevance ranking.
const scoreNode = (node, year) => {
  const typeBonus = node.objectType === 'MOVIE' ? 0 : 1;
  const nodeYear = node?.content?.originalReleaseYear;
  if (!year || !nodeYear) return typeBonus + 1;
  const diff = Math.abs(Number(year) - Number(nodeYear));
  if (diff === 0) return typeBonus;
  if (diff <= 1) return typeBonus + 0.5;
  return typeBonus + 2 + diff;
};

const pickBestGuess = (nodes, year) => [...nodes].sort((a, b) => scoreNode(a, year) - scoreNode(b, year))[0];

const CACHE_KEY = 'cinemapedia-streaming-cache-v1';
const CACHE_TTL = 6 * 60 * 60 * 1000;

const cacheKey = ({ title, year, imdbId, country, language }) =>
  `${country}:${language}:${imdbId || `${(title || '').trim().toLowerCase()}:${year || ''}`}`;

const readCache = (key) => {
  try {
    const hit = JSON.parse(sessionStorage.getItem(CACHE_KEY) || '{}')[key];
    if (hit && Date.now() - hit.at < CACHE_TTL) return hit.value;
  } catch (e) {
    /* storage unavailable */
  }
  return null;
};

const writeCache = (key, value) => {
  try {
    const all = JSON.parse(sessionStorage.getItem(CACHE_KEY) || '{}');
    all[key] = { at: Date.now(), value };
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(all));
  } catch (e) {
    /* storage unavailable */
  }
};

const STORAGE_KEY = 'cinemapedia-streaming-v1';

export const loadStreamCountry = () => {
  try {
    const { country } = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    return COUNTRIES.some((c) => c.code === country) ? country : 'US';
  } catch (e) {
    return 'US';
  }
};

export const saveStreamCountry = (country) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ country }));
  } catch (e) {
    /* storage unavailable */
  }
};

export const fetchStreamingAvailability = async ({ title, year, imdbId, country = 'US', language = 'en' }) => {
  const lang = language === 'ar' ? 'ar' : 'en';
  const key = cacheKey({ title, year, imdbId, country, language: lang });
  const cached = readCache(key);
  if (cached) return cached;

  const res = await fetchData(
    restAPIs.justwatchSearch({
      query: SEARCH_QUERY,
      // JustWatch has no "search by imdbId" filter, so we still search by title text and
      // then confirm the right result below by matching its own reported imdbId — a wider
      // page raises the odds the correct (if less popular) title is actually in the batch.
      variables: { country, language: lang, first: 10, filter: { searchQuery: title } },
    })
  );

  // fetchData never throws: HTTP errors come back as { status, data }.
  if (res?.status || res?.errors?.length || !res?.data) {
    throw new JustWatchError('Could not load streaming info.');
  }

  const nodes = (res.data.popularTitles?.edges || []).map((edge) => edge.node).filter(Boolean);
  const searchUrl = `https://www.justwatch.com/${country.toLowerCase()}/search?q=${encodeURIComponent(title || '')}`;
  if (!nodes.length) {
    const result = { status: 'not_found', searchUrl };
    writeCache(key, result);
    return result;
  }

  let best;
  let uncertain = false;
  if (imdbId) {
    // Authoritative: only ever show data for the exact title CinemaPedia means.
    best = nodes.find((node) => node.content?.externalIds?.imdbId === imdbId);
    if (!best) {
      const result = { status: 'not_found', searchUrl };
      writeCache(key, result);
      return result;
    }
  } else {
    // No imdbId on this record (rare) — best-effort guess by title/year, flagged as such.
    best = pickBestGuess(nodes, year);
    uncertain = true;
  }

  const platforms = groupOffers(best.offers);
  const sourceUrl = best.content?.fullPath ? `https://www.justwatch.com${best.content.fullPath}` : searchUrl;

  const result = platforms.length
    ? { status: 'ok', platforms, uncertain, sourceUrl }
    : { status: 'unavailable', searchUrl, sourceUrl, uncertain };
  writeCache(key, result);
  return result;
};
